import React, { createContext, forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Placement } from '@floating-ui/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName, IconColorType } from '@frontend/icons';
import { classNames } from '@frontend/string';
import { theme } from '@frontend/theme';
import {
  PopoverMenu,
  PopoverMenuItem,
  usePopoverMenu,
  contextFactory,
  ActionButton,
  useEventListener,
  Button,
  ButtonSize,
  PopoverMenuItemDropdown,
} from '@frontend/design-system';

interface ActionsContextValue {
  isInsidePopover: boolean;
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps'];
  index: number;
  handleClosePopover: () => void;
}

const smallButtonStyles = (isSmall: boolean) =>
  isSmall
    ? css`
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      `
    : null;

const ActionsContext = createContext<ActionsContextValue | undefined>(undefined);

export const useActionsContext = () => useContext(ActionsContext);

type AdaptoActionsVariant = 'icon-buttons' | 'action-buttons' | 'secondary-buttons';

interface AdaptoActionsProps {
  children: React.ReactNode;
  maxActions?: number;
  shouldOptimizeSpace?: boolean;
  spacing?: number;
  variant?: AdaptoActionsVariant;
  iconColor?: IconColorType | 'inherit';
  size?: ButtonSize;
  enableCloseEvent?: boolean;
  moreActionsTrackingId?: string;
  popoverPlacement?: Placement;
}

type AdaptoActionsContextValue = {
  variant: AdaptoActionsVariant;
  size?: ButtonSize;
  iconColor?: string;
};

const [Actions, useActions] = contextFactory<AdaptoActionsContextValue>();

const CLOSE_ADAPTO_ACTIONS = 'closeAdaptoActions';

export const AdaptoActions = ({
  children,
  maxActions = 4,
  shouldOptimizeSpace = false,
  spacing = 1,
  variant = 'icon-buttons',
  size = 'large',
  iconColor = 'primary',
  enableCloseEvent,
  moreActionsTrackingId,
  popoverPlacement,
  ...rest
}: AdaptoActionsProps) => {
  const [visibleActionsCount, setVisibleActionsCount] = useState(maxActions - 1);
  const containerRef = useRef<HTMLDivElement>(null);
  const actions = React.Children.toArray(children).filter(Boolean);
  const actionsCount = actions.length;
  const visibleActions = actions.slice(0, visibleActionsCount);
  const popoverActions = actions.slice(visibleActionsCount);

  const optimizeSpace = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const actionWidth = 40;
      const actionSpacing = spacing * 8;
      const maxVisibleActions = Math.floor((containerWidth + actionSpacing) / (actionWidth + actionSpacing)) - 1;
      const remainingActions = actions.length - maxVisibleActions;
      const visibleActionsCount = remainingActions === 1 ? actionsCount : maxVisibleActions;

      setVisibleActionsCount(visibleActionsCount);
    }
  };

  useEffect(() => {
    if (shouldOptimizeSpace) {
      optimizeSpace();

      const resizeObserver = new ResizeObserver(optimizeSpace);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => {
        if (containerRef.current) {
          resizeObserver.unobserve(containerRef.current);
        }
      };
    } else {
      if (actions.length === maxActions) {
        setVisibleActionsCount(maxActions);
        return;
      }

      setVisibleActionsCount(Math.min(maxActions - 1, actions.length));
    }

    return;
  }, [actions.length, maxActions, shouldOptimizeSpace]);

  const getIconColor = () => {
    if (iconColor === 'inherit' || variant === 'secondary-buttons') return undefined;

    return theme.font.colors[iconColor ?? 'default'];
  };
  const getIconSize = () => {
    if (variant === 'secondary-buttons' || variant === 'action-buttons') return undefined;

    return size;
  };
  const renderActions = () => {
    const calculatedColor = getIconColor();
    const calculatedSize = getIconSize();

    return (
      <Actions.Provider value={{ variant, size: calculatedSize, iconColor: calculatedColor }}>
        {visibleActions.map((action, index) => (
          <React.Fragment key={index}>{action}</React.Fragment>
        ))}
        {popoverActions.length > 0 && (
          <MoreActionPopover
            popoverPlacement={popoverPlacement}
            enableCloseEvent={enableCloseEvent}
            actions={popoverActions}
            trackingId={moreActionsTrackingId}
          />
        )}
      </Actions.Provider>
    );
  };

  return (
    <div
      ref={containerRef}
      css={css`
        display: flex;
        gap: ${theme.spacing(spacing)};
        max-width: 100%;
      `}
      onClick={(e) => e.stopPropagation()}
      {...rest}
    >
      {renderActions()}
    </div>
  );
};

interface ActionProps {
  icon: IconName;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disableCloseOnSelect?: boolean;
  subMenuItems?: Pick<React.ComponentProps<typeof PopoverMenuItemDropdown>, 'subMenuItems'>['subMenuItems'];
  trackingId?: string;
}

type Action = Omit<React.ComponentProps<typeof Button>, 'ref' | 'size' | 'onClick' | 'children' | 'icon'> & ActionProps;

type OnClickEvent = React.MouseEventHandler<HTMLButtonElement> & React.MouseEventHandler<HTMLAnchorElement>;

const getColor = ({ disabled, destructive, color }: Pick<Action, 'color' | 'disabled' | 'destructive'>) => {
  if (disabled) return theme.font.colors.disabled;
  if (destructive) return theme.font.colors.error;
  if (!!color) return color;
  return 'inherit';
};

const getIconColor = ({ disabled, destructive, color }: Pick<Action, 'color' | 'disabled' | 'destructive'>) => {
  if (disabled) return theme.font.colors.disabled;
  if (destructive) return theme.font.colors.error;
  return color;
};

export const Action = forwardRef<HTMLButtonElement, Action>(
  (
    {
      disableCloseOnSelect = false,
      label,
      hoverLabel,
      onClick,
      icon,
      showLabelWhenDisabled,
      className,
      subMenuItems,
      ...rest
    },
    ref
  ) => {
    const { isInsidePopover, getItemProps, index, handleClosePopover } = useActionsContext() ?? {};
    const { variant, size, iconColor: defaultIconColor } = useActions();

    const color = getColor({ disabled: rest.disabled, destructive: rest.destructive, color: rest.color || '' });

    const iconColor = getIconColor({
      disabled: rest.disabled,
      destructive: rest.destructive,
      color: rest.color ?? defaultIconColor,
    });

    if (isInsidePopover) {
      const Component = subMenuItems ? PopoverMenuItemDropdown : PopoverMenuItem;
      return (
        <Component
          Icon={() => <Icon name={icon} size={16} />}
          {...(getItemProps?.({
            onClick,
            disableCloseOnSelect,
            index: index ?? 0,
            className: classNames(className, 'action-item action-item--action'),
            hoverLabel,
            showLabelWhenDisabled,
            ...(rest as unknown as React.ComponentProps<typeof PopoverMenuItem>),
          }) ?? {})}
          {...(subMenuItems && {
            subMenuItems,
            showOnHover: true,
            closeParentMenu: handleClosePopover,
          })}
          css={[{ color, svg: { color: iconColor } }]}
          ref={ref}
        >
          {label}
        </Component>
      );
    }

    if (variant === 'icon-buttons') {
      return (
        <Button
          css={[{ color, svg: { color: iconColor } }, smallButtonStyles(size === 'small')]}
          hoverLabel={hoverLabel || label}
          iconName={icon}
          size={size}
          variant='secondary'
          onClick={onClick as OnClickEvent}
          className={rest.disabled ? '' : classNames(className, 'action-item action-item--icon')}
          {...rest}
          ref={ref}
        />
      );
    }

    if (variant === 'secondary-buttons') {
      return (
        <Button
          variant='secondary'
          hoverLabel={hoverLabel || label}
          onClick={onClick}
          className={classNames(className, 'action-item action-item--secondary')}
          {...rest}
          ref={ref}
        >
          <Icon name={icon} size={16} />
        </Button>
      );
    }

    return (
      <ActionButton
        css={{ color, svg: { color } }}
        ref={ref}
        label={hoverLabel || label}
        onClick={onClick}
        className={classNames(className, 'action-item action-item--button')}
        {...rest}
      >
        <Icon name={icon} size={16} />
      </ActionButton>
    );
  }
);

Action.displayName = 'Action';

export const MoreAction = forwardRef<HTMLButtonElement, Omit<ActionProps, 'label'>>(({ onClick, ...rest }, ref) => {
  const { t } = useTranslation();
  const { variant, size, iconColor } = useActions();

  if (variant === 'icon-buttons' || variant === 'secondary-buttons') {
    return (
      <Button
        css={[{ svg: { color: iconColor } }, smallButtonStyles(size === 'small')]}
        size={size}
        iconName='more'
        hoverLabel={t('More')}
        variant='secondary'
        onClick={onClick as OnClickEvent}
        aria-label={t('Action menu')}
        {...rest}
        ref={ref}
      />
    );
  }

  return (
    <ActionButton
      aria-label={t('Action menu')}
      css={{ color: iconColor }}
      label='More'
      onClick={onClick as OnClickEvent}
      {...rest}
      ref={ref}
    >
      <Icon name='more' size={16} />
    </ActionButton>
  );
});

MoreAction.displayName = 'MoreAction';

interface MoreActionPopoverProps {
  actions: React.ReactNode[];
  enableCloseEvent?: boolean;
  trackingId?: string;
  popoverPlacement?: Placement;
}

const MoreActionPopover: React.FC<MoreActionPopoverProps> = ({
  actions,
  enableCloseEvent = false,
  trackingId,
  popoverPlacement,
}) => {
  const { getMenuProps, getTriggerProps, getItemProps, close } = usePopoverMenu({
    placement: popoverPlacement ?? 'bottom-end',
    omitSizeMiddleware: true,
  });

  const handleClosePopover = useCallback(() => {
    close();
  }, [close]);

  useEventListener(CLOSE_ADAPTO_ACTIONS as any, handleClosePopover, enableCloseEvent);

  return (
    <>
      <MoreAction {...getTriggerProps()} trackingId={trackingId} />
      <PopoverMenu {...getMenuProps()} alwaysRender>
        {actions.map((action, index) => (
          <ActionsContext.Provider
            key={index}
            value={{ isInsidePopover: true, getItemProps, index, handleClosePopover }}
          >
            {action}
          </ActionsContext.Provider>
        ))}
      </PopoverMenu>
    </>
  );
};

const closeAdaptoActions = new CustomEvent(CLOSE_ADAPTO_ACTIONS);

export const closeAdaptoActionsPopover = () => {
  window.dispatchEvent(closeAdaptoActions);
};

AdaptoActions.Action = Action;

/**
 * This function will be available only if `enableCloseEvent` props is passed to AdaptoActions wrapper.
 */
AdaptoActions.close = closeAdaptoActionsPopover;
